import {
  calculateCasesSold,
  calculateMasterDisplay,
  calculateOrdered,
  calculateProductAvailable,
  calculateProductOverShort,
  calculateProductRequired,
  calculateSoldCasesPer1000,
  calculateTotalCasesSold
} from "./produceMovementAndRecord.calc";
import {
  CommodityInterface,
  DailyData,
  PreviousWeekCommodityDailyData,
  PreviousWeekCommodityData,
  DailyProductData,
  FutureWeekCommodityData,
  FutureWeekSalesData
} from "./interface";
import {
  addToDate,
  formatDateOfWeek,
  getDateIndex,
  getFormatedDate,
  getNextDayNoonUTC,
  getWeekStartAndEnd,
  isAfterDate,
  isBeforeDateTime,
  isSameOrAfterDate,
  isSameOrBeforeDate
} from "src/utils/getDates";
import {
  DATE_TIME_UNITS,
  defaultDateFormat,
  fullDateTime
} from "src/config/constants/genericConstants";
import { UnitType } from "dayjs";

type PreviousWeekData = {
  [itemId: string]: PreviousWeekCommodityData;
};

const DailyProductData1: any = Array.from(
  { length: 7 },
  (_, i) =>
  ({
    day: i,
    productRequired: null,
    inventoryOnHand: null,
    ordered: null,
    received: null,
    productAvailable: null,
    overShort: null,
    casesSold: null
  } as any)
);

/** function to get values from previous week */
export const getLastWeekCasesSoldPer1000 = (
  itemId: string,
  previousWeekData: PreviousWeekData
): number | null => {
  if (previousWeekData?.[itemId] === undefined) {
    return null;
  }

  const { soldCasesPer1000 = null } = previousWeekData[itemId];
  return soldCasesPer1000;
};

/** ****************************************************************************** */

/** **************************Get generic values ************************ */

export const getDepartmentRetailKey = (departmentId: string) =>
  `${departmentId.toLowerCase()}Retail`;

/** ******************************************************************************* */

const setDailyProductData = (
  commodity: CommodityInterface,
  salesData: {
    [key: number]: DailyData;
  },
  casesPerThousand: number | null,
  previousWeekCommodities: {
    [key: string]: PreviousWeekCommodityData;
  },
  orderWindow: {
    orderDay: string;
    deliveryDay: string;
  }[],
  weekEndingDate: string
) => {
  const dailyProductResult: { [key: number]: DailyProductData } = {};
  const { masterDisplay, dailyProductData = {}, itemId } = commodity;

  const [currentWeekStart, currentWeekEnd] = getWeekStartAndEnd(
    weekEndingDate || "",
    defaultDateFormat
  );

  for (let day = 0; day < 7; day++) {
    const date = addToDate(currentWeekStart, day, "days");
    const dateString = formatDateOfWeek(date, defaultDateFormat);
    const {
      inventoryBackStore = null,
      inventoryCounter = null,
      received = null
    } = dailyProductData[day] ?? {};

    const {
      forecastSales = null,
      actualSales = null,
      orderFactor = null
    } = salesData[day] ?? {};

    const {
      inventoryBackStore: nextDayInventoryBackStore = null,
      inventoryCounter: nextDayInventoryCounter = null
    } = dailyProductData[day + 1] ?? {};

    const productRequired = calculateProductRequired(
      casesPerThousand,
      masterDisplay,
      forecastSales
    );

    let order = null;
    if (salesData[day]?.deliveredBy) {
      const orderWindowData = orderWindow?.find((window) => {
        const deliveryDay = formatDateOfWeek(
          window.deliveryDay,
          defaultDateFormat
        );
        return deliveryDay === dateString;
      });

      const orderDate = orderWindowData?.orderDay;
      const isOrderDayInCurrentWeek =
        isSameOrAfterDate(orderDate, currentWeekStart) &&
        isSameOrBeforeDate(orderDate, currentWeekEnd);

      let orderedDayOverShort = null;
      if (isOrderDayInCurrentWeek) {
        orderedDayOverShort =
          dailyProductResult[getDateIndex(orderDate)].overShort;
      } else {
        orderedDayOverShort =
          previousWeekCommodities[itemId]?.dailyProductData?.[
            getDateIndex(orderDate)
          ]?.overShort ?? null;
      }

      order = calculateOrdered(
        received,
        orderFactor,
        casesPerThousand,
        orderedDayOverShort ?? 0
      );
    }

    const productAvailable = calculateProductAvailable(
      inventoryBackStore,
      inventoryCounter,
      order
    );

    const overShort = calculateProductOverShort(
      productAvailable,
      productRequired
    );

    const casesSold = calculateCasesSold(
      nextDayInventoryBackStore,
      nextDayInventoryCounter,
      productAvailable,
      actualSales
    );

    dailyProductResult[day] = {
      day,
      inventoryBackStore,
      inventoryCounter,
      received,
      productRequired,
      order,
      productAvailable,
      overShort,
      casesSold
    };
  }
  return dailyProductResult;
};

export const setCommodity = (
  commodity: CommodityInterface,
  actualSalesOfTheWeek: number,
  salesData: {
    [key: number]: DailyData;
  },
  previousWeekData: PreviousWeekData,
  casesPerThousand: number | null,
  orderWindow: {
    orderDay: string;
    deliveryDay: string;
  }[],
  weekEndingDate: string
) => {
  const commodityData = {
    uniqueId: commodity.itemId,
    itemId: commodity.itemId,
    description: commodity.description,
    upc: commodity.upc,
    pack: commodity.pack,
    size: commodity.size,
    masterDisplay: commodity?.masterDisplay ?? null,
    isRandomWeight: commodity.isRandomWeight,
    subclassId: commodity.subclassId,
    casesPerThousand: casesPerThousand,
    sortOrder: commodity.sortOrder,
    dailyProductData: setDailyProductData(
      commodity,
      salesData,
      casesPerThousand,
      previousWeekData,
      orderWindow,
      weekEndingDate
    ),
    totalCasesSold: null as number | null,
    soldCasesPer1000: null as number | null
  };

  const totalCasesSold = calculateTotalCasesSold(
    commodityData,
    actualSalesOfTheWeek
  );
  const soldCasesPer1000 = calculateSoldCasesPer1000(
    totalCasesSold,
    actualSalesOfTheWeek
  );

  return {
    ...commodityData,
    totalCasesSold: totalCasesSold,
    soldCasesPer1000: soldCasesPer1000
  };
};

export const setCommodities = (
  commodities: {
    [key: string]: CommodityInterface;
  },
  actualSalesOfTheWeek: number | null,
  salesData: {
    [key: number]: DailyData;
  },
  previousWeekData: PreviousWeekData,
  orderWindow: {
    orderDay: string;
    deliveryDay: string;
  }[],
  weekEndingDate: string,
  casesPerThousandFromPrevWeek?: boolean
) => {
  const updatedCommodities: {
    [key: string]: CommodityInterface;
  } = {};
  Object.keys(commodities).forEach((itemId: string) => {
    let casesPerThousand = commodities[itemId].casesPerThousand;
    if (
      casesPerThousandFromPrevWeek &&
      getLastWeekCasesSoldPer1000(itemId, previousWeekData)
    ) {
      casesPerThousand = getLastWeekCasesSoldPer1000(itemId, previousWeekData);
    }
    updatedCommodities[itemId] = setCommodity(
      commodities[itemId],
      actualSalesOfTheWeek,
      salesData,
      previousWeekData,
      casesPerThousand ?? null,
      orderWindow,
      weekEndingDate
    );
  });
  return updatedCommodities;
};

export const getCommodity = (commodity: any) => {
  return {
    itemId: commodity.itemId,
    description: commodity.description,
    upc: commodity.upc,
    isRandomWeight: commodity.isRandomWeight,
    subClass: commodity.subclassId,
    pack: commodity.pack,
    size: commodity.size,
    casesPer1000: commodity.casesPerThousand,
    masterDisplay: commodity.masterDisplay,
    totalCasesSold: commodity.totalCasesSold,
    soldCasesPer1000: commodity.soldCasesPer1000,
    sortOrder: commodity.sortOrder,
    dailyProductData: getDailyProductData(commodity)
  };
};

export const getCommodities = (commodities: any) => {
  return commodities.map((commodity: any) => getCommodity(commodity));
};

export const getSalesData = (salesData: any) => {
  return Object.values(salesData).map((sale: any) => ({
    day: sale.day,
    forecastSales: sale.forecastSales,
    actualSales: sale.actualSales,
    orderFactor: sale.orderFactor,
    orderedToday: sale.orderedBy,
    receivedToday: sale.deliveredBy
  }));
};

export const getDailyProductData = (commodity: any) => {
  const dailyProductData: any[] = [];
  for (const dailyProduct of DailyProductData1 || []) {
    dailyProductData.push({
      day: dailyProduct.day,
      productRequired:
        commodity.dailyProductData[dailyProduct.day]?.productRequired ?? null,
      inventoryBackStore:
        commodity.dailyProductData[dailyProduct.day]?.inventoryBackStore ??
        null,
      inventoryCounter:
        commodity.dailyProductData[dailyProduct.day]?.inventoryCounter ?? null,
      ordered: commodity.dailyProductData[dailyProduct.day]?.order ?? null,
      received: commodity.dailyProductData[dailyProduct.day]?.received ?? null,
      productAvailable:
        commodity.dailyProductData[dailyProduct.day]?.productAvailable ?? null,
      overShort:
        commodity.dailyProductData[dailyProduct.day]?.overShort ?? null,
      casesSold: commodity.dailyProductData[dailyProduct.day]?.casesSold ?? null
    });
  }
  return dailyProductData;
};

export const calculateDayWiseProductData = (
  commodity: any,
  day: number,
  salesData: any,
  dailySalesData: any
) => {
  commodity[`${day}-prodReq`] = calculateProductRequired(
    commodity.casesPerThousand,
    commodity.masterDisplay,
    salesData.forecastSales
  );
  commodity[`${day}-order`] = calculateOrdered(
    0,
    salesData.orderFactor,
    commodity.casesPerThousand,
    0
  );
  commodity[`${day}-prodAvail`] = calculateProductAvailable(
    commodity[`${day}-inventoryBackStore`],
    commodity[`${day}-inventoryCounter`],
    commodity[`${day}-order`]
  );
  commodity[`${day}-over/short`] = calculateProductOverShort(
    commodity[`${day}-prodAvail`],
    commodity[`${day}-prodReq`]
  );
  commodity[`${day}-casesSold`] = calculateCasesSold(
    commodity[`${day + 1}-inventoryBackStore`],
    commodity[`${day + 1}-inventoryCounter`],
    commodity[`${day}-prodAvail`],
    salesData.actualSales
  );

  commodity[`${day}-inventoryBackStore`] =
    commodity[`${day}-inventoryBackStore`] ?? null;
  commodity[`${day}-inventoryCounter`] =
    commodity[`${day}-inventoryCounter`] ?? null;
};

export const updateCalculatedValuesOfCommodity = (
  commodity: any,
  dailySalesData: any,
  actualSales: number | null
) => {
  for (const dailyData of dailySalesData || []) {
    calculateDayWiseProductData(
      commodity,
      dailyData.day,
      dailyData,
      dailySalesData
    );
  }

  commodity.totalCasesSold = calculateTotalCasesSold(commodity, +actualSales);
  commodity.soldCasesPer1000 = calculateSoldCasesPer1000(
    commodity.totalCasesSold,
    +actualSales
  );
};

export const formatCommodityData = (
  commodities: any,
  carryForwardItems: any,
  minForcastSales: number | null,
  deletedItemIds: any[]
): {
  [key: string]: CommodityInterface;
} => {
  const formattedCommodityData: {
    [key: string]: CommodityInterface;
  } = {};

  // Add items from previousWeekCommodities that are not in commodities
  const currentWeekCommodityItemIds = new Set(
    commodities.map((commodity: any) => commodity?.itemId)
  );

  const selectedCarryForwardItems = carryForwardItems?.filter(
    (carryForwardItem: any) =>
      carryForwardItem?.itemId &&
      !deletedItemIds.includes(carryForwardItem?.itemId) &&
      !currentWeekCommodityItemIds.has(carryForwardItem.itemId)
  );

  selectedCarryForwardItems.sort(
    (itemA: any, itemB: any) => itemA?.sortOrder - itemB?.sortOrder
  );

  const mergedCommodities = [...selectedCarryForwardItems, ...commodities];

  let index = 0;
  for (const eachCommodity of mergedCommodities) {
    const {
      itemId,
      description,
      upc,
      isRandomWeight,
      subClass,
      pack,
      size,
      dailyProductData,
      casesPer1000
    } = eachCommodity ?? ({} as CommodityInterface);
    const calculatedMasterDisplay = calculateMasterDisplay(
      casesPer1000,
      minForcastSales
    );
    const masterDisplay =
      eachCommodity.masterDisplay ?? calculatedMasterDisplay ?? null;

    const commodity: CommodityInterface = {
      itemId,
      description,
      upc,
      isRandomWeight,
      subclassId: subClass,
      pack,
      size,
      masterDisplay,
      casesPerThousand: casesPer1000,
      totalCasesSold: null,
      soldCasesPer1000: null,
      sortOrder: index,
      dailyProductData: dailyProductData?.reduce(
        (
          acc: { [key: number]: DailyProductData },
          eachDailyProduct: DailyProductData,
          i: number
        ) => {
          acc[eachDailyProduct.day] = eachDailyProduct;
          return acc;
        },
        {} as { [key: number]: DailyProductData }
      )
    };

    formattedCommodityData[eachCommodity.itemId] = commodity;
    index++;
  }

  return formattedCommodityData;
};

export const formatPreviousWeekCommodityData = (previousWeekCommodiites: {
  commodities: any;
  actualSales: number | null;
  dailySalesData: any;
}): {
  [key: string]: PreviousWeekCommodityData;
} => {
  const formattedPreviousWeekCommodityData: {
    [key: string]: PreviousWeekCommodityData;
  } = {};

  const {
    commodities = [],
    actualSales = null,
    dailySalesData = []
  } = previousWeekCommodiites || {};

  commodities?.forEach((commodity: any) => {
    formattedPreviousWeekCommodityData[commodity.itemId] = {
      itemId: commodity.itemId,
      casesPerThousand: commodity.casesPerThousand,
      dailyProductData: formatPreviousWeekDailyData(
        commodity.dailyProductData,
        dailySalesData
      ),
      totalCasesSold: commodity.totalCasesSold,
      soldCasesPer1000: commodity.soldCasesPer1000,
      masterDisplay: commodity.masterDisplay,
      actualSales: actualSales
    };
  });

  return formattedPreviousWeekCommodityData;
};

export const formatFutureWeekData = (
  futureWeekData: any,
  orderWindow: {
    orderDay: string;
    deliveryDay: string;
  }[],
  weekEndingDate: string
): FutureWeekCommodityData => {
  const formattedFutureWeekCommodityData: FutureWeekCommodityData = {
    salesData: {},
    totalForecastedSalesTillNextDeliveryDay: 0
  };

  const { dailySalesData = [] } = futureWeekData || {};

  formattedFutureWeekCommodityData.salesData = dailySalesData?.reduce(
    (acc: { [key: number]: FutureWeekSalesData }, dailySales: any) => {
      acc[dailySales.day] = {
        day: dailySales.day,
        forecastSales: dailySales.forecastSales
      };
      return acc;
    },
    {} as { [key: number]: FutureWeekSalesData }
  );

  const nearestDeliveryDay = getNextWeekNearestDeliveryDay(
    orderWindow,
    weekEndingDate
  );

  for (let i = 0; i < nearestDeliveryDay; i++) {
    formattedFutureWeekCommodityData.totalForecastedSalesTillNextDeliveryDay +=
      formattedFutureWeekCommodityData.salesData?.[i]?.forecastSales ?? 0;
  }

  return formattedFutureWeekCommodityData;
};

const formatPreviousWeekDailyData = (
  previousWeekDailyData: any,
  dailySalesData: any
): {
  [key: number]: PreviousWeekCommodityDailyData;
} => {
  const formattedPreviousWeekDailyData: {
    [key: number]: PreviousWeekCommodityDailyData;
  } = {};

  previousWeekDailyData?.forEach((dailyData: any) => {
    formattedPreviousWeekDailyData[dailyData.day] = {
      day: dailyData.day,
      overShort: dailyData.overShort,
      productAvailable: dailyData.productAvailable,
      casesSold: dailyData.casesSold,
      actualSales: dailySalesData[dailyData.day]?.actualSales ?? null
    };
  });

  return formattedPreviousWeekDailyData;
};

export const getDefaultSalesData = (): {
  [key: number]: DailyData;
} => {
  return {
    0: {
      day: 0,
      deliveredBy: false,
      orderedBy: false,
      orderFactor: null,
      forecastSales: null,
      actualSales: null,
      isActualSalesEditable: true
    },
    1: {
      day: 1,
      deliveredBy: false,
      orderedBy: false,
      orderFactor: null,
      forecastSales: null,
      actualSales: null,
      isActualSalesEditable: true
    },
    2: {
      day: 2,
      deliveredBy: false,
      orderedBy: false,
      orderFactor: null,
      forecastSales: null,
      actualSales: null,
      isActualSalesEditable: true
    },
    3: {
      day: 3,
      deliveredBy: false,
      orderedBy: false,
      orderFactor: null,
      forecastSales: null,
      actualSales: null,
      isActualSalesEditable: true
    },
    4: {
      day: 4,
      deliveredBy: false,
      orderedBy: false,
      orderFactor: null,
      forecastSales: null,
      actualSales: null,
      isActualSalesEditable: true
    },
    5: {
      day: 5,
      deliveredBy: false,
      orderedBy: false,
      orderFactor: null,
      forecastSales: null,
      actualSales: null,
      isActualSalesEditable: true
    },
    6: {
      day: 6,
      deliveredBy: false,
      orderedBy: false,
      orderFactor: null,
      forecastSales: null,
      actualSales: null,
      isActualSalesEditable: true
    }
  };
};

export const updateSalesDataWithOrderWindow = (
  salesData: any,
  orderWindow: {
    orderDay: string;
    deliveryDay: string;
  }[],
  weekEndingDate: string
) => {
  const [currentWeekStart, currentWeekEnd] = getWeekStartAndEnd(
    weekEndingDate,
    defaultDateFormat
  );
  const orderDayIndexes = orderWindow
    .filter((window) => {
      const orderDay = window.orderDay;
      return (
        isSameOrAfterDate(orderDay, currentWeekStart) &&
        isSameOrBeforeDate(orderDay, currentWeekEnd)
      );
    })
    .map((window) => getDateIndex(window.orderDay));

  const deliveryDayIndexes = orderWindow
    .filter((window) => {
      const deliveryDay = window.deliveryDay;
      return (
        isSameOrAfterDate(deliveryDay, currentWeekStart) &&
        isSameOrBeforeDate(deliveryDay, currentWeekEnd)
      );
    })
    .map((window) => getDateIndex(window.deliveryDay));

  for (let i = 0; i < 7; i++) {
    if (orderDayIndexes.includes(i)) {
      salesData[i].orderedBy = true;
    } else {
      salesData[i].orderedBy = false;
    }
    if (deliveryDayIndexes.includes(i)) {
      salesData[i].deliveredBy = true;
    } else {
      salesData[i].deliveredBy = false;
    }
  }

  return salesData;
};

export const isPMRJITEditable = (weekEndingDate: string) => {
  const nextDayNoon = getNextDayNoonUTC(weekEndingDate);
  return isBeforeDateTime(
    getFormatedDate(new Date(), fullDateTime),
    nextDayNoon.format(fullDateTime),
    DATE_TIME_UNITS.HOUR as UnitType
  );
};

export const populateCommodities = (commodities: any) => {
  const populatedCommodities: {
    [key: string]: CommodityInterface;
  } = {};
  commodities.forEach((commodity: any, index: number) => {
    populatedCommodities[commodity.itemId] = {
      ...commodity,
      sortOrder: commodity.sortOrder ?? index,
      dailyProductData: commodity.dailyProductData?.map(
        (dailyProduct: any) => ({
          ...dailyProduct,
          order: dailyProduct.ordered
        })
      ),
      casesPerThousand: commodity.casesPer1000,
      subclassId: commodity.subClass
    };
  });
  return populatedCommodities;
};

export const getNextWeekNearestDeliveryDay = (
  orderWindow: {
    orderDay: string;
    deliveryDay: string;
  }[],
  weekEndingDate: string
): number => {
  const [, currentWeekEnd] = getWeekStartAndEnd(
    weekEndingDate,
    defaultDateFormat
  );
  const deliveryDays = orderWindow
    .map((window) => {
      return window.deliveryDay;
    })
    .filter((date) => {
      return isAfterDate(date, currentWeekEnd);
    });

  let nearestDeliveryDay = 7;
  for (const deliveryDay of deliveryDays) {
    const index = getDateIndex(deliveryDay);
    if (nearestDeliveryDay > index) {
      nearestDeliveryDay = index;
    }
  }
  return nearestDeliveryDay;
};

export const produceMovementRecordPayload = (payload: {
  storeName: string;
  storeId: string;
  weekEndingDate: string;
  forecastedSales: number;
  actualSales: number;
  dailySalesData: any;
  commodities: any;
  deletedItems: any;
  createdBy: string;
  department: string;
}) => {
  const {
    storeName,
    storeId,
    weekEndingDate,
    forecastedSales,
    actualSales,
    dailySalesData,
    commodities,
    deletedItems,
    createdBy,
    department
  } = payload;
  return {
    storeName,
    storeId,
    weekEndingDate,
    forecastedSales,
    actualSales,
    dailySalesData,
    commodities,
    deletedItems,
    createdBy,
    department
  };
};

export const formatSearchResutsResponse = (data: any) =>
  data?.map((item: any) => ({
    ...item,
    itemId: item?.itemId?.toString()?.replace(/^0+/, ""),
    description: item?.description,
    subclassId: item?.subClass,
    size: Number.isNaN(+item.size) ? item.size : (+item.size).toFixed(2)
  }));
